import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Container,
  CssBaseline,
  Divider,
  Grid,
  TextField,
  Typography,
  Paper,
  Chip,
  Stack,
  IconButton,
} from "@mui/material";
import CouponsAPI from "../../../api/firebase/CouponsAPI";
import UserProfileContext from "../../../context/userProfile/UserProfileContext";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";

const OrderSummary = ({
  items = [],
  max_width = "sm",
  no_wrap = true,
  order = null,
  selAddress = null,
  onEditAddress = null,
}) => {
  const couponsAPI = new CouponsAPI();
  const [allCoupons, setAllCoupons] = useState([]);
  const [shippingCost, setShippingCost] = useState(0);
  const [couponCodeByUser, setCouponCodeByUser] = useState("");
  const [couponErrorMsg, setCouponErrorMsg] = useState("");
  const [couponApplied, setCouponApplied] = useState(null);
  const [cartAmount, setCartAmount] = useState(0);
  const [isCOD, setIsCOD] = useState(false);
  const [codCharges, setCODCharges] = useState(0);
  const userProfileContext = useContext(UserProfileContext);

  useEffect(() => {
    const fAmount = items.reduce(
      (a, v) => (a = a + v.listPrice * v.quantity),
      0
    );
    setCartAmount(fAmount);
    userProfileContext.setState((prev) =>
      prev?.id || prev?.uid
        ? {
            ...prev,
            codCharges: codCharges,
          }
        : prev
    );

    if (order !== null) {
      setCouponApplied(order.couponApplied ? order.couponApplied : null);
      setShippingCost(order.shippingCost ? order.shippingCost : 0);
      if (order.payment?.method === "cod") {
        setIsCOD(true);
        setCODCharges(order.codCharges ? order.codCharges : 0);
      }
    } else {
      if (fAmount > Number(process.env.REACT_APP_FREE_SHIPPING_ABOVE || 500)) {
        setShippingCost(0);
      } else {
        setShippingCost(60);
      }
    }

    couponsAPI
      .getCoupons()
      .then((res) => {
        if (res.data?.length > 0) {
          setAllCoupons([...res.data]);
        } else {
          setAllCoupons([]);
        }
      })
      .catch((err) => {
        console.log("Error fetching coupons: ", err);
      });

    if (
      order === null &&
      userProfileContext.state?.couponApplied !== undefined &&
      userProfileContext.state?.couponApplied !== null
    ) {
      setCouponApplied({ ...userProfileContext.state.couponApplied });
      setCouponCodeByUser(userProfileContext.state.couponApplied.couponCode);
    }
    if (
      order !== null &&
      order?.couponApplied !== undefined &&
      order?.couponApplied !== null
    ) {
      setCouponApplied({ ...order.couponApplied });
      setCouponCodeByUser(order.couponApplied.couponCode);
    }
  }, []);

  useEffect(() => {
    if (allCoupons.length > 0 && order === null && !userProfileContext.state?.couponApplied) {
      const activeCoupons = allCoupons.filter(
        (coupon) =>
          coupon.isActive === true && coupon.users.length < coupon.maxUses
      );
      const eligible = activeCoupons.filter((obj) => obj.minShopping <= cartAmount);
      if (eligible.length > 0) {
        const _couponToApply = eligible.reduce((prev, curr) => {
          return cartAmount - curr.minShopping < cartAmount - prev.minShopping
            ? curr
            : prev;
        }, { minShopping: 0 });

        if (_couponToApply.minShopping > 0) {
          userProfileContext.setState((prev) =>
            prev?.id || prev?.uid
              ? {
                  ...prev,
                  couponApplied: { ..._couponToApply },
                }
              : prev
          );
          setCouponApplied({ ..._couponToApply });
          setCouponCodeByUser(_couponToApply.couponCode);
        }
      }
    }
  }, [allCoupons]);

  useEffect(() => {
    userProfileContext.setState((prev) =>
      prev?.id || prev?.uid
        ? {
            ...prev,
            codCharges: codCharges,
          }
        : prev
    );
  }, [codCharges]);

  useEffect(() => {
    userProfileContext.setState((prev) =>
      prev?.id || prev?.uid
        ? {
            ...prev,
            shippingCost: shippingCost,
          }
        : prev
    );
  }, [shippingCost]);

  const applyCoupon = () => {
    const filteredCoupon = allCoupons.filter(
      (coupon) =>
        (coupon.id === couponCodeByUser ||
          coupon.couponCode?.toUpperCase() === couponCodeByUser?.toUpperCase()) &&
        coupon.isActive === true
    );

    if (filteredCoupon.length >= 1) {
      const userCoupon = filteredCoupon[0];
      const isValid = cartAmount >= (userCoupon.minShopping || 0);
      if (!isValid) {
        setCouponApplied(null);
        setCouponErrorMsg(
          `Minimum amount to use this coupon is ₹${userCoupon.minShopping}`
        );
        userProfileContext.setState((prev) =>
          prev?.id || prev?.uid
            ? {
                ...prev,
                couponApplied: null,
              }
            : prev
        );
      } else {
        const isExpired = userCoupon.maxUses <= (userCoupon.users?.length || 0);
        if (isExpired) {
          setCouponApplied(null);
          setCouponErrorMsg("Coupon Expired!");
          userProfileContext.setState((prev) =>
            prev?.id || prev?.uid
              ? {
                  ...prev,
                  couponApplied: null,
                }
              : prev
          );
        } else {
          setCouponErrorMsg("");
          setCouponApplied({ ...userCoupon });
          userProfileContext.setState((prev) =>
            prev?.id || prev?.uid
              ? {
                  ...prev,
                  couponApplied: { ...userCoupon },
                }
              : prev
          );
        }
      }
    } else {
      setCouponErrorMsg("Invalid Coupon Code!");
      setCouponApplied(null);
      userProfileContext.setState((prev) =>
        prev?.id || prev?.uid
          ? {
              ...prev,
              couponApplied: null,
            }
          : prev
      );
    }
  };

  const getItemImage = (item) => {
    if (item?.images && Array.isArray(item.images) && item.images.length > 0) {
      const firstImg = item.images[0];
      if (typeof firstImg === "string") return firstImg;
      if (firstImg?.url) return firstImg.url;
    }
    if (item?.image) return item.image;
    return null;
  };

  const finalPayable = Math.max(
    0,
    cartAmount + shippingCost + codCharges - (couponApplied ? Number(couponApplied.amount) || 0 : 0)
  );

  return (
    <Container
      sx={{ padding: 0, overflowX: "hidden", py: {xs: 1, sm: 1.5} }}
      component="main"
      maxWidth={max_width}
    >
      <CssBaseline />
      <Box sx={{ width: "100%" }}>
        {/* Selected Delivery Destination Banner */}
        {selAddress && (
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: "14px",
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "10px",
                  bgcolor: "rgba(47, 93, 126, 0.1)",
                  color: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <LocationOnOutlinedIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    fontSize: "0.68rem",
                    display: "block",
                  }}
                >
                  DELIVERING TO
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a" }} noWrap>
                  {selAddress.name} &bull; {selAddress.pincode}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }} noWrap>
                  {selAddress.address}, {selAddress.city}, {selAddress.state}
                </Typography>
              </Box>
            </Box>
            {onEditAddress && (
              <Button
                size="small"
                variant="outlined"
                onClick={onEditAddress}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.76rem",
                  borderRadius: "8px",
                  py: "2px",
                  px: 1.25,
                  flexShrink: 0,
                }}
              >
                Change
              </Button>
            )}
          </Paper>
        )}

        {/* Order Items Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.75, sm: 2 },
            mb: 2,
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            bgcolor: "#ffffff",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                fontSize: "0.72rem",
              }}
            >
              Order Items ({items.length})
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
              Subtotal: ₹{cartAmount}
            </Typography>
          </Box>

          <Stack spacing={1.5} divider={<Divider sx={{ borderStyle: "dashed" }} />}>
            {items.map((item, index) => {
              const img = getItemImage(item);
              const lineTotal = (Number(item.listPrice) || 0) * (Number(item.quantity) || 1);
              return (
                <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: "10px",
                      bgcolor: "#f8fafc",
                      border: "1px solid #f1f5f9",
                      overflow: "hidden",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 0.5,
                    }}
                  >
                    {img ? (
                      <Box
                        component="img"
                        src={img}
                        alt={item.title}
                        sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    ) : (
                      <ShoppingBagOutlinedIcon sx={{ color: "#94a3b8", fontSize: 22 }} />
                    )}
                  </Box>

                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: "#1e293b",
                        fontSize: "0.85rem",
                        lineHeight: 1.3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25 }}>
                      <Chip
                        label={`Qty: ${item.quantity}`}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          bgcolor: "#f1f5f9",
                          color: "#475569",
                        }}
                      />
                      <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.78rem" }}>
                        &#8377;{item.listPrice} each
                      </Typography>
                    </Stack>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 800, color: "#0f172a", fontSize: "0.92rem", flexShrink: 0 }}
                  >
                    &#8377;{lineTotal}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Paper>

        {/* Price Breakdown Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.25 },
            mb: 2,
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            bgcolor: "#f8fafc",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 800,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontSize: "0.72rem",
              mb: 1.5,
            }}
          >
            Price Details
          </Typography>

          <Stack spacing={1.25}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
                Items Total
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }}>
                &#8377;{cartAmount}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
                Delivery Charges
              </Typography>
              {shippingCost === 0 ? (
                <Chip
                  label="FREE"
                  size="small"
                  sx={{
                    height: 20,
                    bgcolor: "#dcfce7",
                    color: "#166534",
                    fontWeight: 800,
                    fontSize: "0.7rem",
                    borderRadius: "4px",
                  }}
                />
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }}>
                  &#8377;{shippingCost}
                </Typography>
              )}
            </Box>

            {isCOD && codCharges > 0 && (
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
                  COD Handling Fee
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }}>
                  &#8377;{codCharges}
                </Typography>
              </Box>
            )}

            {couponApplied !== null && (
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
                    Coupon Discount
                  </Typography>
                  <Chip
                    label={couponApplied.couponCode || couponCodeByUser}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.68rem",
                      fontWeight: 800,
                      bgcolor: "rgba(47, 93, 126, 0.1)",
                      color: "primary.main",
                      borderRadius: "4px",
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: "#16a34a", fontWeight: 700 }}>
                  - &#8377;{couponApplied.amount}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 0.5 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a" }}>
                Total Payable
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "primary.main" }}>
                &#8377;{finalPayable}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Coupons Accordion */}
        {order === null && (
          <Accordion
            sx={{
              borderRadius: "14px !important",
              border: "1px solid #e2e8f0",
              mb: 2,
              "&:before": { display: "none" },
              boxShadow: "none",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 2,
                py: 0.5,
                fontWeight: 700,
                fontSize: "0.86rem",
                color: "primary.main",
              }}
            >
              {couponApplied ? `Coupon Applied: ${couponApplied.couponCode}` : "Have a coupon code?"}
            </AccordionSummary>

            <AccordionDetails sx={{ p: 2, pt: 0 }}>
              {couponApplied ? (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: "10px",
                    bgcolor: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: "0.85rem", color: "#166534" }}>
                      {couponApplied.couponCode} applied
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#15803d" }}>
                      You saved &#8377;{couponApplied.amount} on this order!
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => {
                      setCouponApplied(null);
                      setCouponCodeByUser("");
                      userProfileContext.setState((prev) =>
                        prev?.id || prev?.uid
                          ? {
                              ...prev,
                              couponApplied: null,
                            }
                          : prev
                      );
                    }}
                    sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.78rem" }}
                  >
                    Remove
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                    <TextField
                      placeholder="ENTER COUPON CODE"
                      size="small"
                      fullWidth
                      value={couponCodeByUser}
                      onChange={(e) => {
                        setCouponCodeByUser(e.target.value.toUpperCase());
                        if (couponErrorMsg) setCouponErrorMsg("");
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          bgcolor: "#f8fafc",
                          fontSize: "0.85rem",
                          fontWeight: 700,
                          letterSpacing: "0.04em",
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={applyCoupon}
                      disabled={!couponCodeByUser.trim()}
                      sx={{
                        borderRadius: "10px",
                        px: 2.5,
                        fontWeight: 700,
                        textTransform: "none",
                      }}
                    >
                      Apply
                    </Button>
                  </Box>

                  {couponErrorMsg && (
                    <Typography variant="caption" sx={{ color: "error.main", fontWeight: 600, display: "block", mb: 1 }}>
                      {couponErrorMsg}
                    </Typography>
                  )}

                  {/* Available Coupons List */}
                  {allCoupons && allCoupons.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 800,
                          color: "text.secondary",
                          display: "block",
                          mb: 1,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        Available Coupons
                      </Typography>
                      <Stack spacing={1}>
                        {allCoupons
                          .filter((c) => c.isActive !== false)
                          .sort((a, b) => {
                            const minA = Number(a.minShopping) || 0;
                            const minB = Number(b.minShopping) || 0;
                            if (minA !== minB) return minA - minB;
                            return (Number(b.amount) || 0) - (Number(a.amount) || 0);
                          })
                          .map((c) => {
                            const canUse = cartAmount >= (c.minShopping || 0);
                            const isThisApplied = couponApplied?.couponCode === c.couponCode;
                            return (
                              <Paper
                                key={c.id || c.couponCode}
                                elevation={0}
                                sx={{
                                  p: 1.25,
                                  borderRadius: "10px",
                                  border: "1px dashed",
                                  borderColor: isThisApplied
                                    ? "success.main"
                                    : canUse
                                    ? "#cbd5e1"
                                    : "#e2e8f0",
                                  bgcolor: isThisApplied
                                    ? "rgba(46, 125, 50, 0.05)"
                                    : canUse
                                    ? "#ffffff"
                                    : "#fafafa",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Box>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                    <Typography
                                      sx={{
                                        fontWeight: 800,
                                        fontSize: "0.78rem",
                                        color: "primary.main",
                                        bgcolor: "rgba(47, 93, 126, 0.1)",
                                        px: 0.75,
                                        py: "1px",
                                        borderRadius: "4px",
                                      }}
                                    >
                                      {c.couponCode}
                                    </Typography>
                                    <Typography sx={{ fontWeight: 700, fontSize: "0.82rem" }}>
                                      Save &#8377;{c.amount}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "text.secondary", display: "block", fontSize: "0.72rem" }}
                                  >
                                    {c.minShopping ? `On orders above ₹${c.minShopping}` : "Special discount"}
                                  </Typography>
                                  {!canUse && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#b45309",
                                        fontWeight: 600,
                                        display: "block",
                                        fontSize: "0.7rem",
                                        mt: 0.25,
                                      }}
                                    >
                                      Add items worth &#8377;{c.minShopping - cartAmount} for this coupon
                                    </Typography>
                                  )}
                                </Box>
                                <Button
                                  size="small"
                                  variant={isThisApplied ? "contained" : "outlined"}
                                  color={isThisApplied ? "success" : "primary"}
                                  disabled={!canUse || isThisApplied}
                                  onClick={() => {
                                    setCouponCodeByUser(c.couponCode);
                                    if (canUse) {
                                      setCouponErrorMsg("");
                                      setCouponApplied({ ...c });
                                      userProfileContext.setState((prev) =>
                                        prev?.id || prev?.uid
                                          ? {
                                              ...prev,
                                              couponApplied: { ...c },
                                            }
                                          : prev
                                      );
                                    }
                                  }}
                                  sx={{
                                    textTransform: "none",
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                    py: "2px",
                                    px: 1.5,
                                    borderRadius: "8px",
                                  }}
                                >
                                  {isThisApplied ? "Applied" : canUse ? "Apply" : "Locked"}
                                </Button>
                              </Paper>
                            );
                          })}
                      </Stack>
                    </Box>
                  )}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Trust Badges */}
        <Paper
          elevation={0}
          sx={{
            p: 1.75,
            borderRadius: "14px",
            bgcolor: "#ffffff",
            border: "1px solid #f1f5f9",
          }}
        >
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={4} textAlign="center">
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <ShieldOutlinedIcon sx={{ color: "primary.main", fontSize: 22, mb: 0.5 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#334155" }}>
                  Razorpay Protected
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4} textAlign="center">
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <VerifiedUserOutlinedIcon sx={{ color: "#16a34a", fontSize: 22, mb: 0.5 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#334155" }}>
                  100% Genuine
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4} textAlign="center">
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <LocalShippingOutlinedIcon sx={{ color: "primary.main", fontSize: 22, mb: 0.5 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#334155" }}>
                  Fast Delivery
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Container>
  );
};

export default OrderSummary;
